import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Download, RefreshCw, ExternalLink, Copy, Palette, Lock, Crown, MessageSquare, QrCode, Sun, Moon } from 'lucide-react';
import { saveAs } from 'file-saver';
import QRCodeLib from 'qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset colors for quick selection
const PRESET_COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'Ivory', hex: '#F0EDE9' },        // CMYK: 0, 1, 3, 6
  { name: 'Cream', hex: '#FFF2BF' },        // CMYK: 0, 5, 25, 0
  { name: 'Royal Blue', hex: '#0052CC' },   // CMYK: 100, 60, 0, 20
  { name: 'Burgundy', hex: '#800033' },     // CMYK: 0, 100, 60, 50
  { name: 'Magenta', hex: '#FF26FF' },      // CMYK: 0, 85, 0, 0
  { name: 'Lime', hex: '#BFFF00' },         // CMYK: 25, 0, 100, 0
  { name: 'Lavender', hex: '#D699F7' },     // CMYK: 16, 40, 3, 0
];

// Premium tiers that have access to color customization
const PREMIUM_TIERS = ['premium', 'pro'];

export default function QRCodePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { adminDarkMode, toggleAdminDarkMode, adminTheme: th } = useTheme();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');
  const [qrColor, setQrColor] = useState('#000000');
  const [savingColor, setSavingColor] = useState(false);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [broadcastUrl, setBroadcastUrl] = useState('');
  const [organizerQrCode, setOrganizerQrCode] = useState('');

  // Check if user has premium access for color customization
  const hasPremiumAccess = event && (PREMIUM_TIERS.includes(event.tier) || (event.is_trial && PREMIUM_TIERS.includes(event.trial_tier)));

  // Handle color click for free users
  const handleColorClick = (color) => {
    if (hasPremiumAccess) {
      handleColorChange(color);
    } else {
      setShowPremiumPopup(true);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
      setQrColor(response.data.qr_color || '#000000');
      const frontendUrl = window.location.origin;
      setPublicUrl(`${frontendUrl}/event/${response.data.slug}`);
      
      // Set broadcast URL and generate organizer QR code
      const broadcastLink = `${frontendUrl}/event/${response.data.slug}/broadcast`;
      setBroadcastUrl(broadcastLink);
      
      // Generate organizer QR code
      try {
        const qrDataUrl = await QRCodeLib.toDataURL(broadcastLink, {
          width: 256,
          margin: 2,
          color: {
            dark: '#D4AF37',
            light: '#ffffff'
          }
        });
        setOrganizerQrCode(qrDataUrl);
      } catch (qrError) {
        console.error('Failed to generate organizer QR code');
      }
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = async (color) => {
    setQrColor(color);
    setSavingColor(true);
    try {
      // Save color to event
      await axios.put(`${API}/events/${eventId}`, { qr_color: color });
      setEvent(prev => ({ ...prev, qr_color: color }));
      
      // Auto-regenerate QR code with new color
      // Pass origin_url to ensure QR code encodes the correct domain
      setGenerating(true);
      const response = await axios.post(`${API}/events/${eventId}/generate-qr`, {
        origin_url: window.location.origin
      });
      setEvent(prev => ({ ...prev, qr_code_url: response.data.qr_code_url, qr_color: color }));
      // Note: We intentionally don't update publicUrl from response.
      // The frontend uses window.location.origin to ensure the URL is always correct
      // for the current environment (preview vs production).
    } catch (error) {
      toast.error('Failed to update QR code');
    } finally {
      setSavingColor(false);
      setGenerating(false);
    }
  };

  const handleGenerateQR = async () => {
    setGenerating(true);
    try {
      // Pass origin_url to ensure QR code encodes the correct domain
      const response = await axios.post(`${API}/events/${eventId}/generate-qr`, {
        origin_url: window.location.origin
      });
      setEvent({ ...event, qr_code_url: response.data.qr_code_url });
      // Note: We intentionally don't update publicUrl from response.
      // The frontend uses window.location.origin to ensure the URL is always correct
      // for the current environment (preview vs production).
      toast.success(t('qrGenerated'));
    } catch (error) {
      toast.error('Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!event?.qr_code_url) {
      toast.error('No QR code available to download');
      return;
    }
    
    // Direct navigation to download endpoint
    // The server sends Content-Disposition: attachment which forces download
    const downloadUrl = `${API}/events/${eventId}/download-qr`;
    
    // Use iframe for download to prevent page navigation
    let iframe = document.getElementById('download-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'download-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }
    iframe.src = downloadUrl;
    
    toast.success('Download started!');
  };

  const handleCopyLink = () => {
    // Fallback method for copying text (works when Clipboard API is blocked)
    const textArea = document.createElement('textarea');
    textArea.value = publicUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen admin-bg flex items-center justify-center transition-colors duration-300">
        <div className="animate-pulse admin-accent">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen admin-bg p-4 md:p-8 transition-colors duration-300">
      {/* Header */}
      <header className="max-w-3xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="admin-text-secondary"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>
          {/* Theme Toggle */}
          <button
            onClick={toggleAdminDarkMode}
            className="p-2 rounded-lg transition-all duration-200 admin-card admin-card-hover admin-icon"
            title={adminDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="theme-toggle-btn"
          >
            {adminDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold admin-accent">
          {t('qrCode')}
        </h1>
        <p className="admin-text-secondary mt-2">
          {event?.name}
        </p>
      </header>

      <main className="max-w-3xl mx-auto">
        <div className="admin-card rounded-xl p-8 text-center">
          {/* QR Code Display */}
          <div className="mb-8">
            {event?.qr_code_url ? (
              <div className="inline-block p-6 bg-white rounded-2xl shadow-lg" data-testid="qr-code-container">
                <img
                  src={event.qr_code_url}
                  alt="QR Code"
                  className="w-64 h-64 mx-auto cursor-pointer"
                  data-testid="qr-code-image"
                  title="Right-click to save image"
                />
                <p className="text-xs text-slate-500 mt-2">Right-click image to save</p>
              </div>
            ) : (
              <div className="inline-block p-6 bg-slate-800/50 rounded-2xl border-2 border-dashed border-slate-700">
                <div className="w-64 h-64 flex items-center justify-center text-slate-500">
                  {t('noQRGenerated')}
                </div>
              </div>
            )}
          </div>

          {/* Color Picker - Premium Feature */}
          <div className="mb-8 max-w-md mx-auto relative">
            <Label className="text-slate-300 flex items-center justify-center gap-2 mb-3">
              <Palette className="h-4 w-4" />
              {t('qrColor')}
              {!hasPremiumAccess && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  <Crown className="h-3 w-3" />
                  Premium
                </span>
              )}
            </Label>
            
            {/* Color Presets - Always visible */}
            <div className="flex flex-wrap gap-2 justify-center mb-3">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.hex}
                  onClick={() => handleColorClick(color.hex)}
                  className={`w-10 h-10 rounded-full border-2 transition-all hover:scale-110 ${
                    qrColor === color.hex ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/50' : 'border-slate-600'
                  }`}
                  style={{ backgroundColor: color.hex }}
                  title={color.name}
                  data-testid={`color-preset-${color.name.toLowerCase()}`}
                />
              ))}
            </div>
            
            {/* Custom Color Input */}
            <div className="flex items-center justify-center gap-3">
              <span className="text-slate-400 text-sm">{t('customColor')}:</span>
              {hasPremiumAccess ? (
                <>
                  <input
                    type="color"
                    value={qrColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-12 h-8 rounded cursor-pointer border border-slate-600"
                    data-testid="color-picker-input"
                    disabled={savingColor || generating}
                  />
                  <input
                    type="text"
                    value={qrColor}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val && !val.startsWith('#')) {
                        val = '#' + val;
                      }
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        setQrColor(val);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        handleColorChange(val);
                      } else {
                        setQrColor(event?.qr_color || '#000000');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                          handleColorChange(val);
                        }
                      }
                    }}
                    className="w-24 px-2 py-1 bg-slate-800/50 border border-slate-600 rounded text-amber-500 font-mono text-sm text-center focus:outline-none focus:border-[#D4AF37]"
                    placeholder="#000000"
                    maxLength={7}
                    data-testid="hex-input"
                    disabled={savingColor || generating}
                  />
                  {(savingColor || generating) && <span className="text-xs text-amber-500">{t('applying')}...</span>}
                </>
              ) : (
                <>
                  <div 
                    onClick={() => setShowPremiumPopup(true)}
                    className="w-12 h-8 rounded border border-slate-600 bg-slate-700 cursor-pointer hover:border-slate-500"
                  />
                  <input
                    type="text"
                    value="#000000"
                    onClick={() => setShowPremiumPopup(true)}
                    readOnly
                    className="w-24 px-2 py-1 bg-slate-800/50 border border-slate-600 rounded text-slate-500 font-mono text-sm text-center cursor-pointer hover:border-slate-500"
                    data-testid="hex-input"
                  />
                </>
              )}
            </div>

            {/* Premium Popup - Only shows when free user clicks on colors */}
            {showPremiumPopup && !hasPremiumAccess && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div 
                  className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm rounded-xl"
                  onClick={() => setShowPremiumPopup(false)}
                />
                <div className="relative bg-slate-900 border border-slate-700 rounded-xl p-5 text-center shadow-xl">
                  <button 
                    onClick={() => setShowPremiumPopup(false)}
                    className="absolute top-2 right-2 text-slate-500 hover:text-white text-lg"
                  >
                    ×
                  </button>
                  <Lock className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm mb-4">{t('qrColorPremiumFeature')}</p>
                  <Link to={`/events/${eventId}`}>
                    <Button 
                      size="sm" 
                      className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                    >
                      <Crown className="h-4 w-4 mr-1" />
                      {t('upgradeToPremium')}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Button
              onClick={handleGenerateQR}
              disabled={generating}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-semibold rounded-full px-8 h-14"
              data-testid="generate-qr-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {event?.qr_code_url ? 'Regenerate' : t('generateQR')}
            </Button>
            {event?.qr_code_url && (
              <a
                href={`${API}/events/${eventId}/download-qr`}
                download={`${event?.slug || 'event'}-qr-code.png`}
                className="inline-flex items-center justify-center border border-slate-700 text-slate-300 hover:bg-slate-800 rounded-full px-8 h-14 font-semibold transition-colors"
                data-testid="download-qr-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download QR
              </a>
            )}
          </div>

          {/* Public Link */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-400 mb-2">{t('publicLink')}</p>
            <div className="flex items-center gap-2 justify-center">
              <code className="text-amber-500 font-mono text-sm break-all" data-testid="public-url">
                {publicUrl}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="text-slate-400 hover:text-white shrink-0"
                data-testid="copy-link-btn"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white shrink-0"
                data-testid="open-link-btn"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Organizer Broadcast Section */}
        <div className="glass-card p-8 mt-6">
          <div className="text-center mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-[#D4AF37] flex items-center justify-center gap-2">
              <MessageSquare className="h-6 w-6" />
              Broadcast to Guests
            </h2>
            <p className="text-slate-400 text-sm mt-2">
              Send real-time announcements to all guests viewing your event page
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Broadcast Link */}
            <div>
              <h3 className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Quick Access Link
              </h3>
              <p className="text-slate-500 text-sm mb-3">
                Bookmark this link on your phone for easy access during your event
              </p>
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <code className="text-amber-500 font-mono text-xs break-all flex-1" data-testid="broadcast-url">
                    {broadcastUrl}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(broadcastUrl);
                      toast.success('Broadcast link copied!');
                    }}
                    className="text-slate-400 hover:text-white shrink-0"
                    data-testid="copy-broadcast-link-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button
                onClick={() => navigate(`/event/${event?.slug}/broadcast`)}
                className="w-full mt-4 bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-semibold"
                data-testid="open-broadcast-btn"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Open Broadcast Panel
              </Button>
            </div>

            {/* Organizer QR Code */}
            <div className="text-center">
              <h3 className="text-slate-300 font-medium mb-3 flex items-center justify-center gap-2">
                <QrCode className="h-4 w-4" />
                Organizer QR Code
              </h3>
              <p className="text-slate-500 text-sm mb-3">
                Scan this QR code to open the broadcast panel
              </p>
              {organizerQrCode ? (
                <div className="inline-block p-4 bg-white rounded-xl shadow-lg">
                  <img
                    src={organizerQrCode}
                    alt="Organizer QR Code"
                    className="w-40 h-40 mx-auto"
                    data-testid="organizer-qr-code"
                  />
                </div>
              ) : (
                <div className="inline-block p-4 bg-slate-800/50 rounded-xl">
                  <div className="w-40 h-40 flex items-center justify-center text-slate-500 text-sm">
                    Loading...
                  </div>
                </div>
              )}
              <p className="text-slate-500 text-xs mt-2">
                Keep this QR code private - it's for organizers only
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
